import { Brand, createBranded } from '@deltasierra/type-utilities';
import { HasId, FullAuditable, FullAuditableKeys, Omit, PathBuilder, HttpMethod, ApiRoute } from '../common';
import { LocationId, LocationShippingDetails } from '../location';
import { Upload, UploadId } from '../upload';
import { BuilderTemplate } from '../builderTemplate';
import { PrintProviderSKUId, PrintProviderSKU } from './PrintProviderSKU';
import { PrintProviderStatsId, PrintProviderStats } from './PrintProviderStats';
import { PrintProviderShoppingCartOrderId } from './PrintProviderShoppingCartOrder';
import { PrintProviderSKUQuantityOptionDto } from './PrintProviderSKUQuantityOption';

export type PrintProviderShoppingCartItemId = Brand<number, 'PrintProviderShoppingCartItemId'>;
export const PrintProviderShoppingCartItemId = createBranded<PrintProviderShoppingCartItemId>();

export interface PrintProviderShoppingCartItem extends HasId<PrintProviderShoppingCartItemId>, FullAuditable {
    quantity: number;
    printProviderSKUId: PrintProviderSKUId;
    printProviderStatsId: PrintProviderStatsId;
    printProviderShoppingCartOrderId: PrintProviderShoppingCartOrderId;
}

export function searchForQuantityOption(
    item: CartItemWithFullDetailsDto,
): PrintProviderSKUQuantityOptionDto | undefined {
    // Bit of a dodgy hack alternative to find / findByPredicate
    return item.printProviderSKU.printProviderSKUQuantityOptions.filter(
        (option: PrintProviderSKUQuantityOptionDto) => option.quantity === item.quantity,
    )[0];
}

export function getItemPriceAmountInCents(item: CartItemWithFullDetailsDto): number | undefined {

    if (item && item.printProviderSKU) {
        if (item.printProviderSKU.linearPricing && !!item.printProviderSKU.priceAmountInCents) {
            return item.quantity * item.printProviderSKU.priceAmountInCents;
        } else {
            const quantityMatch = searchForQuantityOption(item);
            return quantityMatch ? quantityMatch.priceAmountInCents : undefined;
        }
    }
    return undefined;
}

export type PrintProviderShoppingCartItemDto = Omit<PrintProviderShoppingCartItem, FullAuditableKeys>;

export type CartItemWithBasicDetailsDto = Pick<PrintProviderShoppingCartItem, 'id' | 'quantity'> & {
    printProviderSKU: CartItemSKUDetails;
    printProviderStats: {
        locationId: LocationId;
    };
};

export type CartItemWithFullDetailsDto = Pick<
    PrintProviderShoppingCartItemDto,
    'id' | 'printProviderShoppingCartOrderId' | 'quantity'
> & {
    printProviderSKU: CartItemSKUDetails;
    printProviderStats: CartItemStats;
};

type CartItemSKUDetails = Pick<PrintProviderSKU, 'id' | 'linearPricing' | 'priceAmountInCents' | 'sku' | 'skuName'> & {
    printProviderSKUQuantityOptions: PrintProviderSKUQuantityOptionDto[];
};

export type CartItemStats = Pick<PrintProviderStats, 'formatHeight' | 'formatWidth' | 'id' | 'instructions'> & {
    location: LocationShippingDetails;
    publishedArtifact: CartItemArtifactDetails;
};

interface CartItemArtifactDetails {
    artifactId: UploadId;
    artifact: CartItemUploadDetails;
    builderTemplate: CartItemBuilderTemplateDetails;
}

type CartItemUploadDetails = Pick<Upload, 'key' | 'thumb256x256url' | 'url'>;

type CartItemBuilderTemplateDetails = Pick<BuilderTemplate, 'title'>;

export type IUpdatePrintProviderShoppingCartItemBody = Pick<PrintProviderShoppingCartItem, 'quantity'>;

export interface ItemApiParams {
    orderId: PrintProviderShoppingCartOrderId;
    itemId: PrintProviderShoppingCartItemId;
}

function SHOPPING_CART_ITEMS_API_ROOT<T extends ItemApiParams>(path: PathBuilder<T>) {
    return path
        .literal('printProviderShoppingCartOrders')
        .param('orderId')
        .literal('printProviderShoppingCartItems')
        .param('itemId');
}

export const PrintProviderShoppingCartItemsApi = {
    deleteItem: new ApiRoute<void, ItemApiParams, void, void>(HttpMethod.Delete, path =>
        SHOPPING_CART_ITEMS_API_ROOT(path),
    ),
    updateItem: new ApiRoute<void, ItemApiParams, IUpdatePrintProviderShoppingCartItemBody, void>(
        HttpMethod.Patch,
        path => SHOPPING_CART_ITEMS_API_ROOT(path),
    ),
};
